import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage, swallowedFailure } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";

/**
 * Table whitelist mirrored from the API's data-export service
 * (packages/api/src/services/system/data-export/data-export.ts —
 * DEFAULT_TABLES). The service filters any request against this same list,
 * so drift only hides tables, it can never export something new.
 */
export const EXPORT_TABLE_GROUPS: { group: string; tables: string[] }[] = [
  {
    group: "Organization",
    tables: ["clients", "client_modules", "client_tools", "communications"],
  },
  {
    group: "Infrastructure",
    tables: ["client_instances", "docker_hosts", "instance_action_audit"],
  },
  {
    group: "DevOps",
    tables: ["devops_connections", "devops_links", "devops_subscriptions"],
  },
  {
    group: "User / System",
    tables: ["users", "roles", "user_roles", "manufacturing_departments", "notifications", "audit_log"],
  },
];

export const ALL_EXPORT_TABLES: string[] = EXPORT_TABLE_GROUPS.flatMap((g) => g.tables);

interface ClientSummary {
  id: string;
  name?: string;
  code?: string;
}

interface DataExportResult {
  exportedAt: string;
  exportedBy: number | null;
  client: ClientSummary | null;
  counts?: Record<string, number>;
  data?: Record<string, unknown[]>;
}

/**
 * View store for the /admin/data-export view.
 *
 * Drives the admin-only 'data-export' custom service: `action: 'counts'` for a
 * per-table row-count preview and `action: 'export'` for the JSON bundle
 * (the only output format the service implements). Tables carrying a
 * `clientId` column are scoped to the selected client; global tables (users,
 * roles, audit_log, ...) are exported in full. Secret-at-rest columns
 * (password hashes, 2FA secrets, encrypted PATs/webhook secrets) are stripped
 * server-side and never leave the database.
 */
class DataExportStore {
  rootStore: RootStore;

  clientsLoading = false;

  selectedClientId = "";
  selectedTables: Set<string> = new Set(ALL_EXPORT_TABLES);

  counts: Record<string, number> | null = null;
  countsLoading = false;

  exporting = false;
  /** A failed count preview or export. */
  error: string | null = null;
  /** The client directory behind the header picker did not load — without it
   *  no client can be chosen, so the page says so instead of an empty picker. */
  clientsError: string | null = null;
  lastExportedAt: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `data-export` ServiceStore (see serviceStores/admin.stores.ts). */
  private get dataExportService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("data-export") ?? this.rootStore.dataExportStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * The `clients` ServiceStore (owned by the clients domain). Used only for the
   * client picker directory; a null store leaves the picker empty.
   */
  private get clientsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("clients") ?? this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * Client directory for the picker. A computed view over the clients
   * ServiceStore cache (populated by `loadClients`).
   */
  get clients(): ClientSummary[] {
    return (this.clientsService?.entities ?? []) as unknown as ClientSummary[];
  }

  get selectedClient(): ClientSummary | undefined {
    return this.clients.find((c) => c.id === this.selectedClientId);
  }

  get selectedTableList(): string[] {
    return ALL_EXPORT_TABLES.filter((t) => this.selectedTables.has(t));
  }

  get canRun(): boolean {
    return Boolean(this.selectedClientId) && this.selectedTableList.length > 0;
  }

  get allSelected(): boolean {
    return this.selectedTables.size === ALL_EXPORT_TABLES.length;
  }

  isTableSelected(table: string): boolean {
    return this.selectedTables.has(table);
  }

  isGroupSelected(group: string): boolean {
    const tables = EXPORT_TABLE_GROUPS.find((g) => g.group === group)?.tables || [];
    return tables.every((t) => this.selectedTables.has(t));
  }

  countFor(table: string): number | null {
    if (!this.counts) return null;
    return this.counts[table] ?? 0;
  }

  // ---- Selection actions ---------------------------------------------------

  setSelectedClientId(clientId: string) {
    this.selectedClientId = clientId;
    this.counts = null;
    this.error = null;
  }

  toggleTable(table: string) {
    const next = new Set(this.selectedTables);
    if (next.has(table)) next.delete(table);
    else next.add(table);
    this.selectedTables = next;
  }

  toggleGroup(group: string) {
    const tables = EXPORT_TABLE_GROUPS.find((g) => g.group === group)?.tables || [];
    const next = new Set(this.selectedTables);
    if (this.isGroupSelected(group)) tables.forEach((t) => next.delete(t));
    else tables.forEach((t) => next.add(t));
    this.selectedTables = next;
  }

  toggleAll() {
    this.selectedTables = this.allSelected ? new Set() : new Set(ALL_EXPORT_TABLES);
  }

  // ---- Service calls -------------------------------------------------------

  async loadClients() {
    if (this.clientsLoading || this.clients.length > 0) return;
    this.clientsLoading = true;
    this.clientsError = null;
    try {
      // Populates the clients ServiceStore cache, which `clients` reads.
      // findOrThrow: the ServiceStore resolves null on a failed find, which
      // left the picker silently empty.
      await findOrThrow(this.clientsService, {
        query: { $limit: 500, $sort: { name: 1 } },
      });
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.clientsError = loadErrorMessage(err, "Failed to load clients");
      });
    } finally {
      runInAction(() => {
        this.clientsLoading = false;
      });
    }
  }

  /** Preview per-table row counts for the current selection. */
  async loadCounts() {
    if (!this.canRun || this.countsLoading) return;
    this.countsLoading = true;
    this.error = null;
    const ended = sessionMark(this.rootStore);
    try {
      const result = (await this.dataExportService?.create({
        action: "counts",
        clientId: this.selectedClientId,
        tables: this.selectedTableList,
      } as never)) as unknown as DataExportResult | null;
      if (ended()) return;
      // The wrapper swallows a refusal (resolves null, the reason on
      // errorOnCreate): name it, not "Cannot read properties of null".
      if (result == null) throw swallowedFailure(this.dataExportService, "errorOnCreate", "Failed to load counts");
      runInAction(() => {
        this.counts = result.counts || {};
      });
    } catch (err) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.counts = null;
        this.error = err instanceof Error ? err.message : "Failed to load counts";
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.countsLoading = false;
        });
      }
    }
  }

  /** Run the export and download the JSON bundle. */
  async runExport() {
    if (!this.canRun || this.exporting) return;
    this.exporting = true;
    this.error = null;
    // The wrapper resolves null on a sign-out (it swallows the SessionEnded),
    // so the session is asked directly before anything is saved to disk.
    const ended = sessionMark(this.rootStore);
    try {
      const result = (await this.dataExportService?.create({
        action: "export",
        clientId: this.selectedClientId,
        tables: this.selectedTableList,
      } as never)) as unknown as DataExportResult | null;
      if (ended()) return;
      if (result == null) throw swallowedFailure(this.dataExportService, "errorOnCreate", "Export failed");

      const clientLabel = (this.selectedClient?.name || this.selectedClientId)
        .replace(/[^a-z0-9-]+/gi, "-")
        .toLowerCase();
      const stamp = (result.exportedAt || new Date().toISOString()).slice(0, 19).replace(/[:]/g, "-");
      this.downloadJson(result, `data-export_${clientLabel}_${stamp}.json`);

      runInAction(() => {
        this.lastExportedAt = result.exportedAt;
      });
      this.rootStore.notificationStore?.addSuccessNotification(
        "Export downloaded as a JSON bundle"
      );
    } catch (err) {
      if (isSessionEnded(err) || ended()) return;
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Export failed";
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.exporting = false;
        });
      }
    }
  }

  private downloadJson(payload: unknown, filename: string) {
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  async load() {
    await this.loadClients();
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.clientsLoading = false;
    this.selectedClientId = "";
    this.selectedTables = new Set(ALL_EXPORT_TABLES);
    this.counts = null;
    this.countsLoading = false;
    this.exporting = false;
    this.error = null;
    this.clientsError = null;
    this.lastExportedAt = null;
  }
}

export default DataExportStore;
